import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Button,
  Table,
  Group,
  ActionIcon,
  Badge,
  Text,
  Loader,
  Stack,
  Card,
  Modal,
  TextInput,
  Textarea,
  Switch,
  NumberInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconPlus, IconEdit, IconTrash } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  getAllCategoriesAdmin,
  createCategory,
  updateCategory,
  deleteCategory,
  generateSlug,
} from '@/lib/admin/categories';
import { Category } from '@/types';
import AdminLayout from '@/components/admin/AdminLayout';
import { useAuth } from '@/context/AuthContext';

export default function AdminCategoriesPage() {
  const navigate = useNavigate();
  const { user, isAdmin: isAdminUser, loading: authLoading } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [saving, setSaving] = useState(false);

  const form = useForm({
    initialValues: {
      name: '',
      slug: '',
      description: '',
      order: 0,
      isActive: true,
    },
    validate: {
      name: (value) => (value.length < 1 ? 'Name is required' : null),
      slug: (value) => (value.length < 1 ? 'Slug is required' : null),
    },
  });

  useEffect(() => {
    if (!authLoading && (!user || !isAdminUser)) {
      navigate('/xyz/login');
      return;
    }

    if (user && isAdminUser) {
      loadCategories();
    }
  }, [user, isAdminUser, authLoading, navigate]);

  // Auto-generate slug from name when creating new category
  useEffect(() => {
    if (!editingCategory && form.values.name) {
      form.setFieldValue('slug', generateSlug(form.values.name));
    }
  }, [form.values.name, editingCategory]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await getAllCategoriesAdmin();
      setCategories(data);
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to load categories',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    form.reset();
    setModalOpen(true);
  };

  const openEditModal = (category: Category) => {
    setEditingCategory(category);
    form.setValues({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      order: category.order || 0,
      isActive: category.isActive,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (values: typeof form.values) => {
    setSaving(true);
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, values);
        notifications.show({
          title: 'Success',
          message: 'Category updated successfully',
          color: 'green',
        });
      } else {
        await createCategory(values);
        notifications.show({
          title: 'Success',
          message: 'Category created successfully',
          color: 'green',
        });
      }
      setModalOpen(false);
      loadCategories();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to save category',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (categoryId: string, categoryName: string) => {
    if (!confirm(`Are you sure you want to delete "${categoryName}"?`)) {
      return;
    }

    try {
      await deleteCategory(categoryId);
      notifications.show({
        title: 'Success',
        message: 'Category deleted successfully',
        color: 'green',
      });
      loadCategories();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete category',
        color: 'red',
      });
    }
  };

  if (authLoading || (loading && categories.length === 0)) {
    return (
      <AdminLayout>
        <Container size="xl" py="xl">
          <Loader size="lg" style={{ display: 'block', margin: '0 auto' }} />
        </Container>
      </AdminLayout>
    );
  }

  if (!user || !isAdminUser) {
    return null;
  }

  return (
    <AdminLayout>
      <Container size="xl" py="xl">
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={1}>Categories</Title>
            <Button leftSection={<IconPlus size={16} />} onClick={openCreateModal}>
              Add New Category
            </Button>
          </Group>

          {categories.length === 0 ? (
            <Card withBorder padding="xl" radius="md" style={{ textAlign: 'center' }}>
              <Text c="dimmed">No categories found. Create your first category!</Text>
            </Card>
          ) : (
            <Card withBorder padding="md" radius="md">
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Slug</Table.Th>
                    <Table.Th>Order</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {categories.map((category) => (
                    <Table.Tr key={category.id}>
                      <Table.Td>
                        <Text fw={500}>{category.name}</Text>
                        {category.description && (
                          <Text size="xs" c="dimmed" lineClamp={1}>
                            {category.description}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {category.slug}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text>{category.order ?? 0}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={category.isActive ? 'green' : 'gray'}>
                          {category.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <ActionIcon
                            variant="light"
                            color="blue"
                            onClick={() => openEditModal(category)}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                          <ActionIcon
                            variant="light"
                            color="red"
                            onClick={() => handleDelete(category.id, category.name)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Card>
          )}
        </Stack>

        <Modal
          opened={modalOpen}
          onClose={() => setModalOpen(false)}
          title={editingCategory ? 'Edit Category' : 'Create Category'}
          size="md"
        >
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <TextInput
                label="Name"
                placeholder="Category name"
                required
                {...form.getInputProps('name')}
              />

              <TextInput
                label="Slug"
                placeholder="category-slug"
                description="URL-friendly identifier"
                required
                {...form.getInputProps('slug')}
              />

              <Textarea
                label="Description"
                placeholder="Optional description"
                rows={3}
                {...form.getInputProps('description')}
              />

              <NumberInput
                label="Display Order"
                description="Lower numbers appear first"
                min={0}
                {...form.getInputProps('order')}
              />

              <Switch
                label="Active"
                description="Inactive categories won't be shown to customers"
                {...form.getInputProps('isActive', { type: 'checkbox' })}
              />

              <Group justify="flex-end" mt="md">
                <Button variant="subtle" onClick={() => setModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" loading={saving}>
                  {editingCategory ? 'Update' : 'Create'}
                </Button>
              </Group>
            </Stack>
          </form>
        </Modal>
      </Container>
    </AdminLayout>
  );
}

